import { BuildExplorer } from '../commit/build-explorer';
import { HandlerContainer } from './handler-container';
import type { MessageHandler } from './handlers/message-handler';
import {
  isTipTapTextNode,
  type ContentNode,
  type DocumentState,
  type TextNode,
} from './document-types';
import type { WorkspaceRenderBundle } from './workspace-render-bundle';

export type PromptMarkdownRenderParams = {
  readonly graph: BuildExplorer;
  readonly mode?: 'simple' | 'expression';
  readonly workspace?: WorkspaceRenderBundle;
};

/**
 * Renders TipTap {@link DocumentState} to LLM prompt markdown using {@link HandlerContainer}
 * handlers (mentions, expressions, lists, etc.). Shared by {@link EditorState} (graph-based)
 * and workspace editors (domain {@link WorkspaceRenderBundle}).
 */
export class DocumentPromptMarkdownRenderer {
  private repositionedNodes: {
    top: ContentNode[];
    bottom: ContentNode[];
  } = {
    top: [],
    bottom: [],
  };

  private groupedNodes: Map<string, ContentNode[]> = new Map();

  private currentGraph: BuildExplorer | null = null;

  private currentWorkspace: WorkspaceRenderBundle | null = null;

  constructor(
    private readonly handlers: HandlerContainer,
    private readonly messageHandlers: MessageHandler[]
  ) {
    this.initGroupedNodesMap();
  }

  private initGroupedNodesMap(): void {
    this.groupedNodes = new Map();
    this.handlers.getAllGroupHandlers().forEach((handlers) => {
      handlers.forEach((handler) => {
        this.groupedNodes.set(handler.groupId, []);
      });
    });
  }

  private resetRenderState(): void {
    this.repositionedNodes = { top: [], bottom: [] };
    this.initGroupedNodesMap();
  }

  /**
   * Same heuristic as legacy {@link EditorState.hasContent}.
   */
  hasContent(state: DocumentState): boolean {
    this.resetRenderState();
    const content = this.renderNode(state, null, null).trim();
    this.resetRenderState();
    return content.length > 0 && content !== '#';
  }

  /**
   * Same heuristic as legacy {@link EditorState.hasBodyContent}.
   */
  hasBodyContent(state: DocumentState): boolean {
    this.resetRenderState();
    const hasTitle =
      state.content?.[0]?.type === 'heading' &&
      state.content?.[0].attrs?.['level'] === 1;

    const bodyState: DocumentState = {
      type: 'doc',
      content: hasTitle ? state.content?.slice(1) : state.content,
    };

    const content = this.renderNode(bodyState, null, null).trim();
    this.resetRenderState();
    return content.length > 0;
  }

  render(state: DocumentState, params: PromptMarkdownRenderParams): string {
    const { graph, mode, workspace } = params;

    this.currentGraph = graph;
    this.currentWorkspace = workspace ?? null;

    if (workspace !== undefined) {
      workspace.documentAppendix.length = 0;
    }

    this.resetRenderState();

    const mainContent = this.renderNode(state, null, mode ?? null);

    const topNodes = this.repositionedNodes.top
      .map((node) => this.renderNodeDirect(node, null, mode ?? null))
      .join('');

    const bottomNodes = this.repositionedNodes.bottom
      .map((node) => this.renderNodeDirect(node, null, mode ?? null))
      .join('');

    const topGroups: string[] = [];
    const bottomGroups: string[] = [];

    this.handlers.getAllGroupHandlers().forEach((handlers) => {
      handlers.forEach((handler) => {
        const nodes = this.groupedNodes.get(handler.groupId) ?? [];
        const rendered = handler.renderGroup(nodes);

        if (rendered.trim().length > 0) {
          if (handler.position === 'top') {
            topGroups.push(rendered);
          } else if (handler.position === 'bottom') {
            bottomGroups.push(rendered);
          }
        }
      });
    });

    this.currentGraph = null;
    this.currentWorkspace = null;

    let res = [
      ...topGroups,
      topNodes,
      mainContent,
      bottomNodes,
      ...bottomGroups,
    ]
      .filter((s) => s.trim().length > 0)
      .join('\n\n')
      .trim();

    if (workspace !== undefined && workspace.documentAppendix.length > 0) {
      const appendix = workspace.documentAppendix
        .filter((s) => s.trim().length > 0)
        .join('\n\n')
        .trim();
      if (appendix.length > 0) {
        res = res.length > 0 ? `${res}\n\n${appendix}` : appendix;
      }
    }

    if (mode !== 'expression') {
      return res;
    }

    const cleaned = this.escapeForExpressionMode(res);

    return `format_nullable\`${cleaned}\``;
  }

  private renderNode(
    node: DocumentState | ContentNode | TextNode,
    parent: ContentNode | DocumentState | null,
    mode: 'simple' | 'expression' | null
  ): string {
    if (isTipTapTextNode(node)) {
      return this.renderTextNode(node, parent, mode);
    }

    const groupHandlersForType = this.handlers.getGroupHandlers(node.type);
    if (groupHandlersForType) {
      let collected = false;
      for (const handler of groupHandlersForType) {
        if (handler.shouldCollect(node as ContentNode)) {
          const groupNodes = this.groupedNodes.get(handler.groupId) ?? [];
          groupNodes.push(node as ContentNode);
          this.groupedNodes.set(handler.groupId, groupNodes);
          collected = true;
        }
      }
      if (collected) {
        return '';
      }
    }

    const handler = this.handlers.getNodeHandler(node.type);
    if (handler && 'position' in handler && handler.position !== 'inline') {
      this.repositionedNodes[handler.position].push(node as ContentNode);
      return '';
    }

    return this.renderNodeDirect(node, parent, mode ?? null);
  }

  private renderNodeDirect(
    node: DocumentState | ContentNode | TextNode,
    parent: ContentNode | DocumentState | null,
    mode: 'simple' | 'expression' | null
  ): string {
    if (isTipTapTextNode(node)) {
      return this.renderTextNode(node, parent, mode ?? null);
    }

    const handler = this.handlers.getNodeHandler(node.type);
    if (handler) {
      const childContent = this.renderContent(
        node.content ?? [],
        node,
        mode ?? null
      );

      const rendered = handler.render(node, parent, childContent, {
        graph: this.currentGraph ?? BuildExplorer.empty(),
        handlers: this.handlers,
        mode: mode ?? undefined,
        messageHandlers: this.messageHandlers,
        workspace: this.currentWorkspace ?? undefined,
      });

      if (mode === 'expression' && handler.escapeBackticks === false) {
        return `__NO_ESCAPE_START__${rendered}__NO_ESCAPE_END__`;
      }

      return rendered;
    }

    return this.renderContent(node.content ?? [], node, mode ?? null);
  }

  private renderTextNode(
    node: TextNode,
    parent: ContentNode | DocumentState | null,
    mode: 'simple' | 'expression' | null
  ): string {
    let text = node.text;

    if (node.marks && node.marks.length > 0) {
      for (const mark of node.marks) {
        const markHandler = this.handlers.getMarkHandler(mark.type);
        if (markHandler) {
          text = markHandler.apply(text, mark, mode ?? undefined);
        }
      }
    }

    return text;
  }

  private renderContent(
    content: (ContentNode | TextNode)[],
    parent: ContentNode | DocumentState,
    mode: 'simple' | 'expression' | null
  ): string {
    return content
      .map((child) => this.renderNode(child, parent, mode))
      .join('');
  }

  private escapeForExpressionMode(content: string): string {
    const NO_ESCAPE_START = '__NO_ESCAPE_START__';
    const NO_ESCAPE_END = '__NO_ESCAPE_END__';

    let result = '';
    let inNoEscapeSection = false;
    let i = 0;

    while (i < content.length) {
      if (
        !inNoEscapeSection &&
        i + NO_ESCAPE_START.length <= content.length &&
        content.substring(i, i + NO_ESCAPE_START.length) === NO_ESCAPE_START
      ) {
        inNoEscapeSection = true;
        i += NO_ESCAPE_START.length;
        continue;
      }

      if (
        inNoEscapeSection &&
        i + NO_ESCAPE_END.length <= content.length &&
        content.substring(i, i + NO_ESCAPE_END.length) === NO_ESCAPE_END
      ) {
        inNoEscapeSection = false;
        i += NO_ESCAPE_END.length;
        continue;
      }

      if (inNoEscapeSection) {
        result += content[i];
        i++;
        continue;
      }

      if (content[i] === '\\') {
        result += '\\\\';
        i++;
        continue;
      }

      if (content[i] === '`') {
        result += '\\`';
        i++;
        continue;
      }

      if (
        i < content.length - 1 &&
        content[i] === '{' &&
        content[i + 1] === '{'
      ) {
        result += '\\{\\{';
        i += 2;
        continue;
      }

      if (
        i < content.length - 1 &&
        content[i] === '}' &&
        content[i + 1] === '}'
      ) {
        result += '\\}\\}';
        i += 2;
        continue;
      }

      if (content[i] === '{') {
        result += '\\{';
        i++;
        continue;
      }

      if (content[i] === '}') {
        result += '\\}';
        i++;
        continue;
      }

      result += content[i];
      i++;
    }

    return result;
  }
}
